<template>
  <div
    class="d-flex flex-column card mb-10"
    :style="cardStyles"
    :key="item.id_colaboracao"
  >
    <!-- Header -->
    <header class="d-flex" :class="headerClasses" :style="headerStyles">
      <!-- Left side content (username) -->
      <div class="d-flex align-center">
        <!-- Username -->
        <span
          class="font-weight-medium ml-3"
          :class="usernameTextClass"
          :style="usernameStyles"
        >
          {{ displayName }}
        </span>
        <span
          v-if="item.dsc_colaborador_user"
          class="font-weight-black ml-2"
          :style="userHandleStyles"
        >
          {{ item.dsc_colaborador_user }}
        </span>
      </div>

      <!-- Right side icons (Twitter/Threads/Bluesky) -->
      <div v-if="hasRightIcon" class="pa-1 mr-3" :style="iconWrapperStyles">
        <component :is="iconComponent" :style="iconColorStyles" class="custom-svg-icon" />
      </div>
    </header>

    <!-- Content Section -->
    <section class="d-flex align-center flex-grow-1 mx-1">
      <!-- Text Content -->
      <div
        v-if="isTextContent"
        v-html="colabText"
        class="px-6 font-weight-light"
        :style="textContentStyles"
      />

      <!-- Audio Content -->
      <div
        v-else-if="isAudioContent"
        class="d-flex align-center justify-center px-4"
        style="width: 100%"
      >
        <audio
          controls
          controlslist="nodownload"
          autoplay
          style="width: 100%"
          :key="item.url_midia_colaboracao"
        >
          <source :src="item.url_midia_colaboracao" />
        </audio>
      </div>
    </section>

    <!-- Divider Line -->
    <div class="divider-line" :style="dividerStyles"></div>

    <!-- Scrolling Hashtag Ticker -->
    <div class="ticker-container" :style="tickerContainerStyles">
      <div class="ticker-content">
        <span
          v-for="(hashtag, index) in hashtagArray"
          :key="index"
          class="hashtag-item"
          :style="hashtagItemStyles"
        >
          {{ hashtag }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { formatTarjaText } from "@/utils/stringUtils";
import TwitterSvg from "@/assets/twitter.svg";
import BlueskySvg from "@/assets/bluesky.svg";
import ThreadsLogo from "@/assets/threads.svg";

export default {
  props: {
    item: Object,
    posicaoCard: String,
    flg_exibir_uf: Boolean,
  },

  components: {
    TwitterSvg,
    BlueskySvg,
    ThreadsLogo,
  },

  data() {
    return {
      dsc_telefone_producao: null,
      hashtagGapPx: 80,

      platformThemes: {
        twitter: {
          colors: {
            username: "rgb(188,153,200)",
            userHandle: "rgb(188,153,200)",
            bodyText: "rgb(82,32,0)",
            hashtagColor: "rgb(206,64,64)",
            iconBg: "rgb(82,32,0)",
            iconColor: "rgb(188,153,200)",
            cardBgDefault: "rgba(188,153,200,1)",
            headerBgDefault: "rgb(82,32,0)",
            dividerColor: "rgb(82,32,0)",
            tickerBg: "rgba(82,32,0,0)",
            tickerText: "rgb(82,32,0)",
          },
          layout: {
            iconPosition: "right",
            headerJustification: "space-between",
            cardWidth: "840px",
            cardHeight: "240px",
          },
          typography: {
            usernameFontFamily: "Fields Medium",
            bodyFontFamily: "Fields Regular",
            usernameSize: "text-h4",
            userHandleSize: "1.4rem",
            textTransform: "lowercase",
          },
        },

        threads: {
          colors: {
            username: "rgb(218,202,173)",
            userHandle: "rgba(218,202,173,1)",
            bodyText: "rgb(82,32,0)",
            hashtagColor: "rgb(147, 38, 17)",
            iconBg: "rgb(82,32,0)",
            iconColor: "rgb(218,202,173)",
            cardBgDefault: "rgba(218,202,173,1)",
            headerBgDefault: "rgb(82,33,0)",
          },
          layout: {
            iconPosition: "right",
            headerJustification: "space-between",
            cardWidth: "840px",
            cardHeight: "240px",
          },
          typography: {
            usernameFontFamily: "Fields Medium",
            bodyFontFamily: "Fields Medium",
            usernameSize: "text-h4",
            userHandleSize: "1.4rem",
            textTransform: "uppercase",
          },
        },

        bluesky: {
          colors: {
            username: "rgb(218,202,173)",
            userHandle: "rgba(218,202,173,1)",
            bodyText: "rgb(82,32,0)",
            hashtagColor: "rgb(147, 38, 17)",
            iconBg: "rgb(82,32,0)",
            iconColor: "rgb(218,202,173)",
            cardBgDefault: "rgba(218,202,173,1)",
            headerBgDefault: "rgb(82,33,0)",
          },
          layout: {
            iconPosition: "right",
            headerJustification: "space-between",
            cardWidth: "840px",
            cardHeight: "240px",
          },
          typography: {
            usernameFontFamily: "Fields Medium",
            bodyFontFamily: "Fields Medium",
            usernameSize: "text-h4",
            userHandleSize: "1.4rem",
            textTransform: "uppercase",
          },
        },
      },
    };
  },

  async created() {
    await this.getNumTelefoneProducao();
  },

  computed: {
    platformType() {
      const source = this.item.dsc_fonte_colaboracao?.toLowerCase();
      const platformMap = {
        twitter: "twitter",
        threads: "threads",
        bluesky: "bluesky",
      };
      return platformMap[source] || "twitter";
    },

    currentTheme() {
      return this.platformThemes[this.platformType] || this.platformThemes.twitter;
    },

    hashtagArray() {
      const baseHashtag = "# saiajustanognt";
      return Array(50).fill(baseHashtag);
    },

    hasRightIcon() {
      return true;
    },

    isTextContent() {
      return !this.item.dsc_tipo_midia_colaboracao;
    },

    isAudioContent() {
      return this.item.dsc_tipo_midia_colaboracao === "audio";
    },

    displayName() {
      if (this.flg_exibir_uf && this.item.dsc_uf_colaborador) {
        return `${this.item.dsc_nome_colaborador} - ${this.item.dsc_uf_colaborador}`;
      }
      return this.item.dsc_nome_colaborador;
    },

    iconComponent() {
      const iconMap = {
        twitter: "TwitterSvg",
        threads: "ThreadsLogo",
        bluesky: "BlueskySvg",
      };
      return iconMap[this.platformType] || "TwitterSvg";
    },

    bodyTextSize() {
      const numChars = this.item.dsc_texto_colaboracao.trim().length;
      const sizeMap = {
        40: "2.1rem",
        80: "2rem",
        120: "2rem",
        160: "1.9rem",
        200: "1.8rem",
        240: "1.6rem",
        280: "1.5rem",
      };

      for (const [threshold, size] of Object.entries(sizeMap)) {
        if (numChars <= threshold) return size;
      }
      return "1.5rem";
    },

    lineHeight() {
      const numChars = this.item.dsc_texto_colaboracao.trim().length;
      const lineHeightMap = {
        40: "2.6rem",
        80: "2.5rem",
        120: "2.4rem",
        160: "2.3rem",
        200: "2.2rem",
        240: "2.1rem",
        280: "1.95rem",
      };

      for (const [threshold, height] of Object.entries(lineHeightMap)) {
        if (numChars <= threshold) return height;
      }
      return "1.5rem";
    },

    colabText() {
      return formatTarjaText(this.item.dsc_texto_colaboracao, "papoDeSegunda");
    },

    headerClasses() {
      return this.currentTheme.layout.iconPosition === "right"
        ? "justify-space-between align-center"
        : "";
    },

    headerStyles() {
      return {
        backgroundColor: this.currentTheme.colors.headerBgDefault,
      };
    },

    iconWrapperStyles() {
      return {
        backgroundColor: this.currentTheme.colors.iconBg,
        width: "46px",
        height: "46px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };
    },

    iconColorStyles() {
      return {
        fill: `${this.currentTheme.colors.iconColor} !important`,
        color: `${this.currentTheme.colors.iconColor} !important`,
        width: "34px",
        height: "34px",
      };
    },

    usernameTextClass() {
      return this.currentTheme.typography.usernameSize;
    },

    usernameStyles() {
      return {
        color: this.currentTheme.colors.username,
        fontFamily: `${this.currentTheme.typography.usernameFontFamily} !important`,
        textTransform: this.currentTheme.typography.textTransform,
      };
    },

    userHandleStyles() {
      return {
        color: this.currentTheme.colors.userHandle,
        fontFamily: `${this.currentTheme.typography.usernameFontFamily} !important`,
        fontSize: this.currentTheme.typography.userHandleSize,
        opacity: 1,
      };
    },

    textContentStyles() {
      return {
        color: this.currentTheme.colors.bodyText,
        fontFamily: this.currentTheme.typography.bodyFontFamily,
        lineHeight: this.lineHeight,
        fontSize: this.bodyTextSize,
        "--hashtag-color": this.currentTheme.colors.hashtagColor,
      };
    },

    dividerStyles() {
      return {
        width: "100%",
        height: "1px",
        backgroundColor: this.currentTheme.colors.dividerColor,
        margin: "0",
      };
    },

    tickerContainerStyles() {
      return {
        width: "100%",
        height: "40px",
        backgroundColor: this.currentTheme.colors.tickerBg,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        position: "relative",
      };
    },

    hashtagItemStyles() {
      return {
        marginRight: `${this.hashtagGapPx}px`,
        display: "inline-block",
        color: `${this.currentTheme.colors.tickerText} !important`,
        fontFamily: `Owners !important`,
        fontSize: "1.38rem !important",
        fontWeight: "400 !important",
        textTransform: `${this.currentTheme.typography.textTransform} !important`,
        whiteSpace: "nowrap",
        flexShrink: 0,
      };
    },

    cardStyles() {
      const positionStyles = this.cardPositionStyles;
      const backgroundStyle = `background-color: ${this.currentTheme.colors.cardBgDefault}`;
      const sizeStyles = `width: ${this.currentTheme.layout.cardWidth}; height: ${this.currentTheme.layout.cardHeight}`;

      return [positionStyles, backgroundStyle, sizeStyles]
        .filter((style) => style)
        .join("; ");
    },

    cardPositionStyles() {
      const positionMap = {
        esquerda: !this.item.url_midia_colaboracao
          ? "margin-left: 100px; justify-self: flex-start"
          : "margin-left: 120px; justify-self: flex-start",
        centro: "justify-self: center",
        direita: !this.item.url_midia_colaboracao
          ? "margin-right: 40px; justify-self: flex-end"
          : "margin-right: 60px; justify-self: flex-start",
      };
      return positionMap[this.posicaoCard] || "";
    },
  },

  methods: {
    async getNumTelefoneProducao() {
      const cod_producao = localStorage.getItem("@socialcam/selected_option_production");
      try {
        const response = await apiClient.get(`/listarDadosProducao/${cod_producao}`);
        this.dsc_telefone_producao = response.data.producao.dsc_num_telefone;
      } catch (error) {
        console.error(error);
      }
    },

    updatePlatformTheme(platform, themeUpdates) {
      if (this.platformThemes[platform]) {
        this.platformThemes[platform] = {
          ...this.platformThemes[platform],
          ...themeUpdates,
        };
      }
    },

    updateCurrentPlatformColors(colorUpdates) {
      if (this.platformThemes[this.platformType]) {
        this.platformThemes[this.platformType].colors = {
          ...this.platformThemes[this.platformType].colors,
          ...colorUpdates,
        };
      }
    },

    updateHashtagGap(newGapPx) {
      this.hashtagGapPx = newGapPx;
    },
  },

  watch: {
    item: async function () {
      await this.getNumTelefoneProducao();
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Fields Medium";
  src: url("@/assets/SaiaJusta/fonts/fields_medium.otf") format("opentype");
}

@font-face {
  font-family: "Fields Regular";
  src: url("@/assets/SaiaJusta/fonts/fields_regular.otf") format("opentype");
}

@font-face {
  font-family: "Owners";
  src: url("@/assets/SaiaJusta/fonts/owners_regular.otf") format("opentype");
}

.card {
  border-radius: 1px;
}

.divider-line {
  flex-shrink: 0;
}

.ticker-container {
  flex-shrink: 0;
}

.ticker-content {
  animation: scroll-left 200s linear infinite;
  transform: translateX(0);
  display: flex;
  align-items: center;
}

@keyframes scroll-left {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

.hashtag-item {
  all: unset;
  display: inline-block !important;
  flex-shrink: 0 !important;
  white-space: nowrap !important;
}

.custom-svg-icon {
  ::v-deep svg {
    fill: inherit !important;
    color: inherit !important;
  }

  ::v-deep path {
    fill: inherit !important;
    color: inherit !important;
  }
}

// Fix hashtag styling ONLY in text content area - be specific to avoid affecting header
section .px-6 ::v-deep span {
  color: var(--hashtag-color) !important;
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: inherit !important;
  line-height: inherit !important;
  text-transform: inherit !important;
}
</style>
